import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const sections = [
  { id: 'who', title: '1. Who We Are' },
  { id: 'collect', title: '2. What Data We Collect' },
  { id: 'use', title: '3. How We Use Your Data' },
  { id: 'ai', title: '4. AI Processing' },
  { id: 'storage', title: '5. Data Storage' },
  { id: 'rights', title: '6. Your Rights' },
  { id: 'retention', title: '7. Data Retention' },
  { id: 'third-party', title: '8. Third-Party Services' },
  { id: 'cookies', title: '9. Cookies' },
  { id: 'children', title: '10. Children' },
  { id: 'changes', title: '11. Changes to this Policy' },
  { id: 'contact', title: '12. Contact' },
];

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#e5e5e5' }}>
      <style>{`
        .legal-h2 { font-family: 'Courier Prime', monospace; font-weight: 700; font-size: 1.05rem; text-transform: uppercase; letter-spacing: 0.1em; color: #E8A020; margin-top: 2.5rem; margin-bottom: 0.75rem; }
        .legal-p { font-family: 'Inter', sans-serif; font-size: 0.88rem; color: #b0b0b0; line-height: 1.75; margin-bottom: 0.9rem; }
        .legal-li { font-family: 'Inter', sans-serif; font-size: 0.88rem; color: #b0b0b0; line-height: 1.75; margin-bottom: 0.4rem; }
        .legal-toc-link { font-family: 'Inter', sans-serif; font-size: 0.82rem; color: #808080; text-decoration: none; display: block; padding: 4px 0; transition: color 0.18s; }
        .legal-toc-link:hover { color: #E8A020; }
        .legal-back { background: none; border: none; color: #606060; font-family: 'Courier Prime', monospace; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.14em; cursor: pointer; padding: 0; display: flex; align-items: center; gap: 6px; transition: color 0.18s; }
        .legal-back:hover { color: #E8A020; }
        .legal-table { width: 100%; border-collapse: collapse; font-family: 'Inter', sans-serif; font-size: 0.82rem; color: #b0b0b0; margin-bottom: 0.9rem; }
        .legal-table th { text-align: left; font-family: 'Courier Prime', monospace; font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.15em; color: #606060; padding: 8px 12px; border-bottom: 1px solid rgba(255,255,255,0.06); }
        .legal-table td { padding: 8px 12px; border-bottom: 1px solid rgba(255,255,255,0.04); vertical-align: top; }
      `}</style>

      {/* Header */}
      <header style={{ borderBottom: '1px solid rgba(232,160,32,0.1)', padding: '16px clamp(16px,4vw,48px)', position: 'sticky', top: 0, background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(12px)', zIndex: 40 }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button className="legal-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} /> Back
          </button>
          <div style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1rem', color: '#E8A020', letterSpacing: '0.14em' }}>
            PANGAJI
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: 'clamp(32px,6vw,64px) clamp(16px,4vw,48px)' }}>
        {/* Hero */}
        <div style={{ marginBottom: '48px' }}>
          <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.25em', color: '#E8A020', marginBottom: '12px' }}>
            Legal
          </p>
          <h1 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: 'clamp(1.8rem,4vw,2.8rem)', letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: '16px' }}>
            Privacy Policy
          </h1>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#606060' }}>
            Last updated: May 2026 &nbsp;·&nbsp; Effective date: May 2026
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2.5fr)', gap: '48px', alignItems: 'start' }}>
          {/* Table of contents */}
          <div style={{ position: 'sticky', top: '80px', background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.06)', padding: '20px' }}>
            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.22em', color: '#606060', marginBottom: '14px' }}>
              Contents
            </p>
            {sections.map(s => (
              <a key={s.id} href={`#${s.id}`} className="legal-toc-link">{s.title}</a>
            ))}
          </div>

          {/* Body */}
          <div>
            <section id="who">
              <h2 className="legal-h2">1. Who We Are</h2>
              <p className="legal-p">
                Pangaji is operated by <strong style={{ color: '#e5e5e5' }}>LAJ House Limited</strong>, a company registered in Kenya (PVT-3B15M26E), based in Nairobi. We provide a story development platform for filmmakers and storytellers.
              </p>
              <p className="legal-p">
                For any privacy-related queries, contact us at <a href="mailto:jamdany@lajhouseltd.com" style={{ color: '#E8A020', textDecoration: 'none' }}>jamdany@lajhouseltd.com</a>.
              </p>
            </section>

            <section id="collect">
              <h2 className="legal-h2">2. What Data We Collect</h2>
              <p className="legal-p">We collect the following categories of data when you use Pangaji:</p>
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td><strong style={{ color: '#e5e5e5' }}>Account data</strong></td><td>Your name and email address, which you provide during registration.</td></tr>
                  <tr><td><strong style={{ color: '#e5e5e5' }}>Payment data</strong></td><td>Your payment method type and transaction records. We do not store card numbers — these are handled by Paystack.</td></tr>
                  <tr><td><strong style={{ color: '#e5e5e5' }}>Content data</strong></td><td>Story content you create on the platform: characters, plots, world building, notes, and related material.</td></tr>
                  <tr><td><strong style={{ color: '#e5e5e5' }}>Usage data</strong></td><td>Information about how you interact with the platform (pages visited, features used, session activity) for product improvement.</td></tr>
                </tbody>
              </table>
            </section>

            <section id="use">
              <h2 className="legal-h2">3. How We Use Your Data</h2>
              <p className="legal-p">We use your data only for the following purposes:</p>
              <ul style={{ margin: '0 0 0.9rem 1.2rem', padding: 0 }}>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>To provide the Service</strong> — storing and displaying your projects, processing your requests, and enabling platform features.</li>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>To process payments</strong> — handling subscription billing via Paystack.</li>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>To communicate with you</strong> — sending account-related emails (receipts, password resets, important service updates). We do not send marketing emails without your explicit consent.</li>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>To improve the product</strong> — analysing aggregated, anonymised usage data to understand how Pangaji is used and where to improve.</li>
              </ul>
              <p className="legal-p">
                We do not sell your personal data. We do not share it with third parties except as described in Section 8.
              </p>
            </section>

            <section id="ai">
              <h2 className="legal-h2">4. AI Processing</h2>
              <p className="legal-p">
                Pangaji's AI Mentor feature sends portions of your story content to the <strong style={{ color: '#e5e5e5' }}>Google Gemini API</strong> to generate coaching responses. This processing happens in real time when you use the AI Mentor.
              </p>
              <p className="legal-p">
                Your content is used solely to generate the coaching response returned to you. It is not used to train AI models by Google or by us. Google's data processing is governed by their API Terms of Service and Privacy Policy.
              </p>
              <p className="legal-p">
                By using the AI Mentor, you acknowledge and consent to your content being processed by the Google Gemini API for the purpose of generating coaching responses.
              </p>
            </section>

            <section id="storage">
              <h2 className="legal-h2">5. Data Storage</h2>
              <p className="legal-p">
                Your data is stored on <strong style={{ color: '#e5e5e5' }}>MongoDB Atlas</strong>, hosted on Amazon Web Services (AWS) servers in the United States. Data is encrypted in transit using TLS and encrypted at rest.
              </p>
              <p className="legal-p">
                The Pangaji application is hosted on <strong style={{ color: '#e5e5e5' }}>Vercel</strong> (frontend) and <strong style={{ color: '#e5e5e5' }}>Render</strong> (backend). By using the Service, you consent to your data being stored and processed in these locations.
              </p>
            </section>

            <section id="rights">
              <h2 className="legal-h2">6. Your Rights</h2>
              <p className="legal-p">You have the following rights regarding your personal data:</p>
              <ul style={{ margin: '0 0 0.9rem 1.2rem', padding: 0 }}>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>Access</strong> — request a copy of the personal data we hold about you.</li>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>Correction</strong> — request correction of inaccurate or incomplete data.</li>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>Deletion</strong> — request deletion of your account and associated data.</li>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>Portability</strong> — request a copy of your content data in a portable format.</li>
              </ul>
              <p className="legal-p">
                To exercise any of these rights, email <a href="mailto:jamdany@lajhouseltd.com" style={{ color: '#E8A020', textDecoration: 'none' }}>jamdany@lajhouseltd.com</a>. We will respond within 30 days.
              </p>
            </section>

            <section id="retention">
              <h2 className="legal-h2">7. Data Retention</h2>
              <p className="legal-p">
                We retain your account data for as long as your account is active. If you request account deletion, we will permanently delete your personal data and content within 30 days, except where we are required to retain certain records by law (e.g. billing records for tax compliance).
              </p>
              <p className="legal-p">
                Payment transaction records are retained for 7 years in accordance with Kenyan tax and financial regulations.
              </p>
            </section>

            <section id="third-party">
              <h2 className="legal-h2">8. Third-Party Services</h2>
              <p className="legal-p">We use the following third-party services to operate Pangaji:</p>
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Purpose</th>
                    <th>Data Shared</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong style={{ color: '#e5e5e5' }}>Paystack</strong></td>
                    <td>Payment processing</td>
                    <td>Email, payment method, billing amount</td>
                  </tr>
                  <tr>
                    <td><strong style={{ color: '#e5e5e5' }}>Google Gemini</strong></td>
                    <td>AI coaching responses</td>
                    <td>Story content you submit to the AI Mentor</td>
                  </tr>
                  <tr>
                    <td><strong style={{ color: '#e5e5e5' }}>Vercel</strong></td>
                    <td>Frontend hosting</td>
                    <td>Web traffic / request logs</td>
                  </tr>
                  <tr>
                    <td><strong style={{ color: '#e5e5e5' }}>Render</strong></td>
                    <td>Backend hosting</td>
                    <td>Server logs</td>
                  </tr>
                  <tr>
                    <td><strong style={{ color: '#e5e5e5' }}>MongoDB Atlas (AWS)</strong></td>
                    <td>Database storage</td>
                    <td>All account and content data</td>
                  </tr>
                  <tr>
                    <td><strong style={{ color: '#e5e5e5' }}>Vercel Web Analytics</strong></td>
                    <td>Aggregate page traffic</td>
                    <td>Page URL, referrer, country, device type. No cookies, no identifier.</td>
                  </tr>
                </tbody>
              </table>
              <p className="legal-p">
                Each third-party provider is bound by their own privacy policy and data protection obligations. We select providers that meet appropriate standards of data security.
              </p>
            </section>

            <section id="cookies">
              <h2 className="legal-h2">9. Cookies</h2>
              <p className="legal-p">
                Pangaji uses a <strong style={{ color: '#e5e5e5' }}>session cookie</strong> to keep you logged in. That is the only cookie we set. We do not use third-party product analytics, session recording, advertising trackers, cross-site tracking, ad networks, or fingerprinting technologies, and we never sell your data. The only traffic measurement is <strong style={{ color: '#e5e5e5' }}>Vercel Web Analytics</strong> (Section 8): aggregate page views with no cookie and no identifier.
              </p>
              <p className="legal-p">
                You can configure your browser to block or delete cookies, but this may affect your ability to stay logged in to the Service.
              </p>
              <p className="legal-p">
                <strong style={{ color: '#e5e5e5' }}>How we measure whether Pangaji works:</strong> from our own database, and only as dates.
                Your account records when you signed up, first created a project, first developed a beat, first opened the script editor, first started a checkout and first paid.
                We count those dates in aggregate. The words you write — beats, characters, scenes, world entries, script pages — are never part of any measurement and never leave Pangaji for it.
              </p>
            </section>

            <section id="children">
              <h2 className="legal-h2">10. Children</h2>
              <p className="legal-p">
                Pangaji is not intended for use by persons under the age of 13. We do not knowingly collect personal data from children under 13. If you believe a child has provided us with personal data, please contact us and we will delete it promptly.
              </p>
            </section>

            <section id="changes">
              <h2 className="legal-h2">11. Changes to this Policy</h2>
              <p className="legal-p">
                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. When we make material changes, we will notify you by email or by a prominent notice in the Service, and update the "Last updated" date at the top of this page.
              </p>
              <p className="legal-p">
                Your continued use of the Service after changes take effect constitutes your acceptance of the updated policy.
              </p>
            </section>

            <section id="contact">
              <h2 className="legal-h2">12. Contact</h2>
              <p className="legal-p">
                For any questions, concerns, or data requests regarding this Privacy Policy, contact us at:
              </p>
              <div style={{ background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.06)', padding: '20px', fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#b0b0b0', lineHeight: 1.75 }}>
                <strong style={{ color: '#e5e5e5' }}>LAJ House Limited</strong><br />
                Registration: PVT-3B15M26E<br />
                Nairobi, Kenya<br />
                Email: <a href="mailto:jamdany@lajhouseltd.com" style={{ color: '#E8A020', textDecoration: 'none' }}>jamdany@lajhouseltd.com</a>
              </div>
              <p className="legal-p" style={{ marginTop: '1rem' }}>
                This policy is governed by and construed in accordance with the laws of the Republic of Kenya.
              </p>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid rgba(232,160,32,0.1)', marginTop: '64px', paddingTop: '24px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <a href="/terms" style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.7rem', color: '#606060', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            onMouseOver={e => e.target.style.color='#E8A020'} onMouseOut={e => e.target.style.color='#606060'}>
            Terms of Service
          </a>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', fontFamily: 'Courier Prime, monospace', fontSize: '0.7rem', color: '#606060', cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase', padding: 0 }}
            onMouseOver={e => e.target.style.color='#E8A020'} onMouseOut={e => e.target.style.color='#606060'}>
            Home
          </button>
          <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.7rem', color: '#404040', marginLeft: 'auto', letterSpacing: '0.05em' }}>
            © 2026 LAJ House Limited
          </p>
        </div>
      </div>
    </div>
  );
}
